import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  Mail,
  Globe,
  Instagram,
  Send,
  MessageCircle,
  MapPin,
  ShieldCheck,
  Clock,
  Building2,
} from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { useLang } from "@/hooks/use-language";
import { toast } from "sonner";
import { submitContactMessage } from "@/lib/contact.functions";
import { SOCIAL } from "@/lib/social-links";
import { TikTokIcon, FacebookIcon } from "@/components/icons/SocialIcons";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/contact" },
      { title: "Contact Us | تواصل معنا — Jozori" },
      {
        name: "description",
        content:
          "Get in touch with the Jozori team in Dubai — parents, partnerships, press, and educators welcome.",
      },
      { property: "og:title", content: "Contact Us | تواصل معنا" },
      {
        property: "og:description",
        content:
          "Reach the people behind Jozori. We respond personally — usually within 24 hours.",
      },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/contact" }],
  }),
  component: ContactPage,
});

const OFFICE_ADDRESS_LINES = [
  "Tamani Arts Offices, Office 1511",
  "Business Bay",
  "Dubai, United Arab Emirates",
];
const OFFICE_ADDRESS_LINES_AR = [
  "تماني آرتس أوفيسز، مكتب ١٥١١",
  "الخليج التجاري",
  "دبي، الإمارات العربية المتحدة",
];
const MAPS_LINK =
  "https://maps.google.com/maps/place//data=!4m2!3m1!1s0x3e5f69268414659b:0x57b0c10714f75064";
const MAPS_EMBED =
  "https://www.google.com/maps?q=Tamani+Arts+Offices+Business+Bay+Dubai&output=embed";

const COPY = {
  en: {
    eyebrow: "Contact",
    title: "We'd love to hear from you.",
    lede:
      "Whether you're a parent, an educator, or a partner — we read every message ourselves and reply personally, usually within 24 hours.",
    form: {
      name: "Your name",
      email: "Email",
      subject: "Subject",
      message: "How can we help?",
      send: "Send message",
      sending: "Sending…",
      success: "Thanks — we'll be in touch within 24 hours.",
      error: "Sorry, something went wrong. Please try again or email us directly.",
    },
    officeTitle: "Visit our office",
    officeBadge: "Headquarters",
    directions: "Open in Google Maps",
    channelsTitle: "Other ways to reach us",
    channels: [
      { icon: Mail, label: "info@jozori.com", href: "mailto:info@jozori.com" },
      { icon: Globe, label: "www.jozori.com", href: "https://www.jozori.com" },
      { icon: Instagram, label: SOCIAL.instagram.label, href: SOCIAL.instagram.href },
      { icon: TikTokIcon, label: SOCIAL.tiktok.label, href: SOCIAL.tiktok.href },
      { icon: FacebookIcon, label: SOCIAL.facebook.label, href: SOCIAL.facebook.href },
    ],
    trust: [
      { icon: ShieldCheck, label: "Your data is encrypted in transit." },
      { icon: Clock, label: "Typical response time: under 24 hours." },
      { icon: Building2, label: "Real office, real team — based in Dubai." },
    ],
  },
  ar: {
    eyebrow: "تواصل",
    title: "يسعدنا أن نسمع منك.",
    lede:
      "سواء كنت من الأهل أو من التربويين أو من الشركاء — نقرأ كل رسالة بأنفسنا، ونرد عليها شخصيًّا خلال ٢٤ ساعة عادةً.",
    form: {
      name: "اسمك",
      email: "البريد الإلكتروني",
      subject: "الموضوع",
      message: "كيف يمكننا مساعدتك؟",
      send: "إرسال الرسالة",
      sending: "جارٍ الإرسال…",
      success: "شكرًا لك — سنعود إليك خلال ٢٤ ساعة.",
      error: "عذرًا، حدث خطأ. حاول مجددًا أو راسلنا مباشرة على البريد الإلكتروني.",
    },
    officeTitle: "زُر مكتبنا",
    officeBadge: "المقر الرئيسي",
    directions: "افتح في خرائط جوجل",
    channelsTitle: "قنوات تواصل أخرى",
    channels: [
      { icon: Mail, label: "info@jozori.com", href: "mailto:info@jozori.com" },
      { icon: Globe, label: "www.jozori.com", href: "https://www.jozori.com" },
      { icon: Instagram, label: SOCIAL.instagram.label, href: SOCIAL.instagram.href },
      { icon: TikTokIcon, label: SOCIAL.tiktok.label, href: SOCIAL.tiktok.href },
      { icon: FacebookIcon, label: SOCIAL.facebook.label, href: SOCIAL.facebook.href },
    ],
    trust: [
      { icon: ShieldCheck, label: "بياناتك مشفّرة أثناء الإرسال." },
      { icon: Clock, label: "وقت الردّ المعتاد: أقل من ٢٤ ساعة." },
      { icon: Building2, label: "فريق حقيقي ومكتب فعلي في دبي." },
    ],
  },
} as const;

function ContactPage() {
  const { lang } = useLang();
  const t = COPY[lang];
  const isAr = lang === "ar";
  const [sending, setSending] = useState(false);
  const submitFn = useServerFn(submitContactMessage);
  const mountedAtRef = useRef<number>(Date.now());

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setSending(true);
    try {
      await submitFn({
        data: {
          name: String(data.get("name") || ""),
          email: String(data.get("email") || ""),
          subject: String(data.get("subject") || ""),
          message: String(data.get("message") || ""),
          locale: lang,
          website: String(data.get("website") || ""),
          elapsedMs: Date.now() - mountedAtRef.current,
        },
      });
      toast.success(t.form.success);
      form.reset();
      mountedAtRef.current = Date.now();
    } catch (err) {
      console.error(err);
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  }

  const addressLines = isAr ? OFFICE_ADDRESS_LINES_AR : OFFICE_ADDRESS_LINES;

  return (
    <SiteShell active="/contact">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10"
        />
        <div
          className="relative mx-auto w-full max-w-3xl px-5 text-center"
          style={{
            paddingTop: "max(3rem, env(safe-area-inset-top))",
            paddingBottom: "2.5rem",
          }}
        >
          <p className="mb-3 text-[11px] font-black uppercase tracking-[0.25em] text-primary">
            {t.eyebrow}
          </p>
          <h1
            className={`text-3xl sm:text-4xl md:text-6xl font-black leading-tight mb-4 ${
              isAr ? "font-display-ar" : "font-display-en"
            }`}
          >
            {t.title}
          </h1>
          <p className="text-base sm:text-lg text-foreground/70 leading-relaxed">{t.lede}</p>

        </div>
      </section>

      {/* FORM + ASIDE */}
      <section
        className="mx-auto w-full max-w-6xl px-5 grid gap-8 md:gap-10 md:grid-cols-[1.4fr_1fr]"
        style={{ paddingBottom: "max(4rem, env(safe-area-inset-bottom))" }}
      >
        {/* Form */}
        <form
          onSubmit={onSubmit}
          className={`relative w-full rounded-3xl border border-clay/40 bg-card p-5 sm:p-8 md:p-10 shadow-sm space-y-5 overflow-hidden ${
            isAr ? "text-right" : ""
          }`}
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <Field name="name" label={t.form.name} required isAr={isAr} autoComplete="name" />
            <Field
              name="email"
              type="email"
              label={t.form.email}
              required
              isAr={isAr}
              autoComplete="email"
            />
          </div>
          <Field name="subject" label={t.form.subject} isAr={isAr} />
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-foreground/60 mb-2">
              {t.form.message}
            </label>
            <textarea
              name="message"
              required
              minLength={5}
              maxLength={5000}
              rows={6}
              dir={isAr ? "rtl" : "ltr"}
              className="w-full rounded-xl border border-clay/40 bg-background px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Honeypot — hidden from humans */}
          <div className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
            <label>
              Leave this field empty
              <input type="text" name="website" tabIndex={-1} autoComplete="off" />
            </label>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 font-black shadow hover:opacity-90 disabled:opacity-60 min-h-12"
          >
            <Send className={`size-4 ${isAr ? "scale-x-[-1]" : ""}`} aria-hidden="true" />
            {sending ? t.form.sending : t.form.send}
          </button>

          {/* Trust strip */}
          <ul className="grid gap-2 pt-2 text-xs text-foreground/60">
            {t.trust.map((row) => {
              const Icon = row.icon;
              return (
                <li key={row.label} className="flex items-center gap-2">
                  <Icon className="size-3.5 text-primary shrink-0" aria-hidden="true" />
                  <span>{row.label}</span>
                </li>
              );
            })}
          </ul>
        </form>

        {/* Channels */}
        <aside className={`space-y-6 ${isAr ? "text-right" : ""}`}>
          <div>
            <h2 className="text-2xl font-black mb-4 inline-flex items-center gap-2">
              <MessageCircle className="size-5 text-primary" />
              {t.channelsTitle}
            </h2>
            <ul className="space-y-2.5">
              {t.channels.map((c) => {
                const Icon = c.icon;
                return (
                  <li key={c.label}>
                    <a
                      href={c.href}
                      target={c.href.startsWith("http") ? "_blank" : undefined}
                      rel="noreferrer"
                      className="flex items-center gap-3 rounded-xl border border-clay/40 bg-card px-4 py-3 font-bold hover:bg-clay/20 min-h-12"
                    >
                      <Icon className="size-4 text-primary shrink-0" aria-hidden="true" />
                      <span className="truncate">{c.label}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Office card */}
          <div className="rounded-2xl border border-clay/40 bg-card overflow-hidden">
            <div className="p-5">
              <span className="inline-block text-[10px] font-black uppercase tracking-[0.2em] text-primary mb-2">
                {t.officeBadge}
              </span>
              <h3 className="text-lg font-black mb-3 inline-flex items-center gap-2">
                <MapPin className="size-4 text-primary" aria-hidden="true" />
                {t.officeTitle}
              </h3>
              <address className="not-italic text-sm text-foreground/75 leading-relaxed space-y-0.5">
                {addressLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </address>
              <a
                href={MAPS_LINK}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline"
              >
                {t.directions} →
              </a>
            </div>
            {/* Map iframe — desktop only. On mobile it looks like an embedded browser; the link above opens the native Maps app instead. */}
            <div className="hidden md:block aspect-[4/3] w-full border-t border-clay/30">
              <iframe
                title="Jozori office — Tamani Arts, Business Bay, Dubai"
                src={MAPS_EMBED}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
                allowFullScreen
              />
            </div>
          </div>
        </aside>
      </section>
    </SiteShell>
  );
}

function Field({
  name,
  label,
  type = "text",
  required,
  isAr,
  autoComplete,
}: {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  isAr: boolean;
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-widest text-foreground/60 mb-2">
        {label}
      </label>
      <input
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        dir={isAr ? "rtl" : "ltr"}
        maxLength={type === "email" ? 254 : 200}
        className="w-full rounded-xl border border-clay/40 bg-background px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </div>
  );
}
